import { c as _c } from "react/compiler-runtime";
import { Body } from '../typography';
import Dot from './Dot';
import { FlexContainer } from './FlexContainer';
import type { ComponentProps } from 'react';
export interface TimelineEvent {
  id: string;
  title: string;
  description?: React.ReactNode;
  /** A node as well as a string, so a step can be stamped with a `DateDisplay`. */
  timestamp?: React.ReactNode;
  metadata?: Record<string, unknown>;
}
export interface TimelineItemProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  timestamp?: React.ReactNode;
  dotVariant?: ComponentProps<typeof Dot>['variant'];
  dotSize?: ComponentProps<typeof Dot>['size'];
  isLast?: boolean;
  className?: string;
}
export function TimelineItem(t0) {
  const $ = _c(22);
  const {
    title,
    description,
    timestamp,
    dotVariant: t1,
    dotSize: t2,
    isLast: t3,
    className
  } = t0;
  const dotVariant = t1 === undefined ? "light" : t1;
  const dotSize = t2 === undefined ? "md" : t2;
  const isLast = t3 === undefined ? false : t3;
  const t4 = `gap-4 w-full relative ${className || ""}`;
  let t5;
  if ($[0] !== dotSize || $[1] !== dotVariant) {
    t5 = <Dot size={dotSize} variant={dotVariant} />;
    $[0] = dotSize;
    $[1] = dotVariant;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== isLast) {
    t6 = !isLast && <div className="absolute top-3.5 h-[calc(100%+1rem)] w-px bg-border-secondary" />;
    $[3] = isLast;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t5 || $[6] !== t6) {
    t7 = <FlexContainer className="mt-1.5" direction="column" items="center">{t5}{t6}</FlexContainer>;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== title) {
    t8 = typeof title === "string" ? <Body className="text-content-primary">{title}</Body> : title;
    $[8] = title;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== description) {
    t9 = description && <>{typeof description === "string" ? <Body className="text-content-secondary" size="sm">{description}</Body> : description}</>;
    $[10] = description;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== timestamp) {
    t10 = timestamp && <>{typeof timestamp === "string" ? <Body className="text-content-tertiary" size="sm">{timestamp}</Body> : timestamp}</>;
    $[12] = timestamp;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t8 || $[16] !== t9) {
    t11 = <FlexContainer className="gap-1" data-slot="timeline-item-content" direction="column">{t8}{t9}{t10}</FlexContainer>;
    $[14] = t10;
    $[15] = t8;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t4 || $[20] !== t7) {
    t12 = <FlexContainer className={t4} data-slot="timeline-item">{t7}{t11}</FlexContainer>;
    $[18] = t11;
    $[19] = t4;
    $[20] = t7;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}
export interface TimelineProps<T extends TimelineEvent = TimelineEvent> {
  events: T[];
  renderItem?: (event: T, index: number, isLast: boolean) => React.ReactNode;
  className?: string;
  itemClassName?: string;
  getDotVariant?: (event: T) => ComponentProps<typeof Dot>['variant'];
  getDotSize?: (event: T) => ComponentProps<typeof Dot>['size'];
}
export default function Timeline(t0) {
  const $ = _c(15);
  const {
    events,
    renderItem,
    className,
    itemClassName,
    getDotVariant,
    getDotSize
  } = t0;
  if (events.length === 0) {
    return null;
  }
  const t1 = `w-full gap-4 ${className || ""}`;
  let t2;
  if ($[0] !== events || $[1] !== getDotSize || $[2] !== getDotVariant || $[3] !== itemClassName || $[4] !== renderItem) {
    let t3;
    if ($[6] !== events.length || $[7] !== getDotSize || $[8] !== getDotVariant || $[9] !== itemClassName || $[10] !== renderItem) {
      t3 = (event, index) => {
        const isLast = index === events.length - 1;
        if (renderItem) {
          return <div key={event.id}>{renderItem(event, index, isLast)}</div>;
        }
        return <TimelineItem className={itemClassName} description={event.description} dotSize={getDotSize?.(event) || "md"} dotVariant={getDotVariant?.(event) || "light"} isLast={isLast} key={event.id} timestamp={event.timestamp} title={event.title} />;
      };
      $[6] = events.length;
      $[7] = getDotSize;
      $[8] = getDotVariant;
      $[9] = itemClassName;
      $[10] = renderItem;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = events.map(t3);
    $[0] = events;
    $[1] = getDotSize;
    $[2] = getDotVariant;
    $[3] = itemClassName;
    $[4] = renderItem;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[12] !== t1 || $[13] !== t2) {
    t3 = <FlexContainer className={t1} direction="column">{t2}</FlexContainer>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}